import { useEffect, useState } from "react";
import {
	Button,
	Dialog,
	DialogActions,
	DialogContent,
	DialogTitle,
	Grid2,
	TextField,
	Typography,
} from "@mui/material";
import type { Recipe } from "../models/Recipe";

interface DuplicateRecipeModalProps {
	open: boolean;
	recipe: Recipe | null;
	existingRecipes: Recipe[];
	onDuplicate: (recipeId: number, name: string) => void;
	onCancel: () => void;
	isDuplicating: boolean;
}

export const DuplicateRecipeModal = ({
	open,
	recipe,
	existingRecipes,
	onDuplicate,
	onCancel,
	isDuplicating,
}: DuplicateRecipeModalProps) => {
	const [name, setName] = useState("");

	useEffect(() => {
		if (open) {
			setName("");
		}
	}, [open]);

	const nameTaken = existingRecipes.some(
		(r) =>
			r.ProductSizeId === recipe?.ProductSizeId &&
			r.Name.trim().toLowerCase() === name.trim().toLowerCase(),
	);
	const canDuplicate = !!name.trim() && !nameTaken;

	const handleSubmit = () => {
		if (!recipe?.Id || !canDuplicate) return;
		onDuplicate(recipe.Id, name.trim());
	};

	if (!recipe) return null;

	const productLabel = recipe.ProductTypeName
		? `(${recipe.ProductTypeName}) ${recipe.ProductSizeName}`
		: recipe.ProductSizeName;

	return (
		<Dialog open={open} onClose={onCancel} maxWidth="sm" fullWidth>
			<DialogTitle sx={{ fontWeight: 600 }}>DUPLICATE RECIPE</DialogTitle>
			<DialogContent>
				<Grid2 container spacing={1.5} sx={{ mt: 0.5 }}>
					<Grid2 size={{ xs: 12 }}>
						<Typography variant="body2" color="text.secondary">
							Source: <strong>{recipe.Name}</strong> - {productLabel}
						</Typography>
					</Grid2>
					<Grid2 size={{ xs: 12 }}>
						<TextField
							label="New Name"
							value={name}
							onChange={(e) => setName(e.target.value)}
							error={nameTaken}
							helperText={
								nameTaken
									? "This product already has a recipe with this name"
									: ""
							}
							fullWidth
							required
							size="small"
							slotProps={{ inputLabel: { shrink: true } }}
						/>
					</Grid2>
				</Grid2>
			</DialogContent>
			<DialogActions sx={{ px: 3, pb: 2 }}>
				<Button
					variant="contained"
					onClick={onCancel}
					sx={{
						backgroundColor: "#e0e0e0",
						color: "rgba(0, 0, 0, 0.87)",
						"&:hover": { backgroundColor: "#d5d5d5" },
					}}
				>
					CANCEL
				</Button>
				<Button
					color="primary"
					variant="contained"
					onClick={handleSubmit}
					disabled={!canDuplicate || isDuplicating}
				>
					{isDuplicating ? "DUPLICATING..." : "DUPLICATE"}
				</Button>
			</DialogActions>
		</Dialog>
	);
};
